import React from "react";
import { Crown, Medal } from "lucide-react";
import {
  computePeriodScore,
  dominantDomain,
  formatScore,
  leaderDisplayName,
  memberInitials,
  MIN_REWARD_WEEKLY_SCORE,
  PODIUM_META,
  WEEKLY_REWARDS,
} from "./leaderboardHelpers";

function PodiumSlot({ rank, row }) {
  const meta = PODIUM_META[rank];
  const prize = WEEKLY_REWARDS[rank];

  if (!row) {
    return (
      <article className={`alb-podium-slot alb-podium-${meta.tone} alb-podium-empty`}>
        <div className="alb-podium-hero-band" aria-hidden>
          <div className="alb-podium-crown-slot" />
          <div className="alb-podium-identity">
            <span className="alb-podium-avatar alb-podium-avatar-empty">—</span>
            <strong className="alb-podium-name">Open slot</strong>
          </div>
        </div>
        <div className="alb-podium-pedestal">
          <div className="alb-podium-pedestal-inner">
            <span className="alb-podium-rank">#{rank}</span>
            <span className="alb-podium-label">{meta.label}</span>
          </div>
        </div>
      </article>
    );
  }

  const score = computePeriodScore(row);
  const lead = dominantDomain(row);
  const displayName = leaderDisplayName(row);

  return (
    <article className={`alb-podium-slot alb-podium-${meta.tone}`}>
      <div className="alb-podium-hero-band">
        <div className="alb-podium-crown-slot">
          {rank === 1 ? <Crown size={16} className="alb-podium-crown" aria-hidden /> : null}
        </div>
        <div className="alb-podium-identity">
          <span className="alb-podium-avatar-wrap">
            <span className="alb-podium-avatar">{memberInitials(row.name, row.inviteCode)}</span>
            <span className="alb-podium-rank-badge">#{rank}</span>
          </span>
          <strong className="alb-podium-name" title={displayName}>
            {displayName}
          </strong>
          <span className="alb-podium-level">{row.userLevel || "Rookie"}</span>
        </div>
      </div>

      <div className="alb-podium-pedestal">
        <div className="alb-podium-pedestal-inner">
          <span className="alb-podium-rank">
            {rank === 1 ? <Medal size={12} aria-hidden /> : null}
            #{rank} · {meta.label}
          </span>
          <div className="alb-podium-score-block">
            <strong className="alb-podium-score tabular-nums">{formatScore(score)}</strong>
            <span className="alb-podium-score-unit">pts</span>
          </div>
          {lead ? (
            <span className="alb-podium-lead" style={{ color: lead.color }}>
              {lead.label} lead
            </span>
          ) : (
            <span className="alb-podium-lead-spacer" aria-hidden />
          )}
          <div className="alb-podium-foot">
            {row.inviteCode ? <code className="alb-podium-code">{row.inviteCode}</code> : null}
            {prize && score >= MIN_REWARD_WEEKLY_SCORE ? (
              <span className="alb-podium-prize">
                {prize.CASH > 0 ? <em>{prize.CASH} CASH</em> : null}
                {prize.COIN > 0 ? <em>{prize.COIN} COIN</em> : null}
              </span>
            ) : prize ? (
              <span className="alb-podium-prize alb-podium-prize-locked">
                Needs {MIN_REWARD_WEEKLY_SCORE} pts
              </span>
            ) : null}
          </div>
        </div>
      </div>
    </article>
  );
}

export default function LeaderboardPodium({ topThree = [] }) {
  const order = [2, 1, 3];

  return (
    <section className="alb-podium" aria-label="Top three players">
      <header className="alb-podium-head">
        <strong>Top 3</strong>
        <span>Current period leaders · {MIN_REWARD_WEEKLY_SCORE}+ pts to get paid</span>
      </header>
      <div className="alb-podium-stage">
        <div className="alb-podium-glow alb-podium-glow-gold" aria-hidden />
        <div className="alb-podium-glow alb-podium-glow-blue" aria-hidden />
        <div className="alb-podium-floor">
          {order.map((rank) => (
            <PodiumSlot key={rank} rank={rank} row={topThree[rank - 1]} />
          ))}
        </div>
      </div>
    </section>
  );
}
